import { datadogRum } from "@datadog/browser-rum";
import { openPopupWindow } from "@utils/utils";
import React, { useEffect, useRef, useState } from "react";

import { Button, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  ProviderZoomIntegrationType,
  useCompleteZoomConnectMutation,
  useStartZoomConnectMutation,
} from "@api/graphql/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

interface ZoomIntegrationProps {
  zoomIntegrationData?: ProviderZoomIntegrationType | null;
  onIntegrationComplete: () => Promise<void>;
}

export const ZoomIntegration: React.FC<ZoomIntegrationProps> = ({ zoomIntegrationData, onIntegrationComplete }) => {
  const [loading, setLoading] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const popupCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { mutateAsync: startZoomConnection } = useStartZoomConnectMutation();
  const { mutateAsync: completeZoomConnection } = useCompleteZoomConnectMutation();

  const completeIntegration = async (code: string) => {
    try {
      const response = await completeZoomConnection({ code });
      if (response?.completeZoomConnect?.status) {
        notifySuccess("Zoom account connected successfully!");
        datadogRum.addAction("Zoom Integration Completed");
      } else {
        notifyError("Failed to connect Zoom account. Please try again.");
        datadogRum.addError("Zoom Integration Failed", { response });
      }
    } catch (err) {
      notifyError("Failed to connect Zoom account. Please try again.");
      datadogRum.addError("Zoom Integration Failed", { error: err });
    } finally {
      await onIntegrationComplete();
      setLoading(false);
    }
  };

  const handleConnectZoom = async () => {
    setLoading(true);
    const response = await startZoomConnection({});
    const url = response?.startZoomConnect?.url;
    if (!url) {
      setLoading(false);
      return;
    }
    const zoomPopup = openPopupWindow(url, "zoom-auth", 600, 700);
    if (!zoomPopup) {
      datadogRum.addError("Zoom Popup Blocked");
      notifyError("Popup blocked! Please allow popups for this website.");
      setLoading(false);
      return;
    }
    popupRef.current = zoomPopup;
    if (popupRef.current) {
      popupCheckIntervalRef.current = setInterval(() => {
        if (popupRef.current?.closed) {
          if (popupCheckIntervalRef.current) {
            clearInterval(popupCheckIntervalRef.current);
          }
          setLoading(false);
        }
      }, 300);
    }
  };

  const handleMessage = (event: MessageEvent) => {
    // Security check
    if (event.origin !== window.location.origin) return;
    if (event.data.type !== "ZOOM_AUTH_CODE" && event.data.type !== "ZOOM_AUTH_ERROR") return;

    if (event.data.type === "ZOOM_AUTH_CODE") {
      completeIntegration(event.data.code);
    } else if (event.data.type === "ZOOM_AUTH_ERROR") {
      datadogRum.addError("Zoom Integration Failed", { error: event.data.error });
      notifyError("Failed to connect Zoom account. Please try again.");
    }

    popupRef.current?.close();
    if (popupCheckIntervalRef.current) {
      clearInterval(popupCheckIntervalRef.current);
    }
    setLoading(false);
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    return () => {
      popupRef.current?.close();
      if (popupCheckIntervalRef.current) {
        clearInterval(popupCheckIntervalRef.current);
      }
    };
  }, []);

  const getZoomCloudRecordingStatus = () => {
    if (!zoomIntegrationData) {
      datadogRum.addError("Zoom Cloud Recording Status is queried but Integration Data Missing", {
        zoomIntegrationData,
      });
      return null;
    }
    const hasCloudRecording = zoomIntegrationData.hasCloudRecording;
    if (hasCloudRecording === null || hasCloudRecording === undefined) {
      return (
        <Paragraph>
          Zoom connected, but we could not verify cloud recording settings (missing Zoom permissions).
        </Paragraph>
      );
    } else if (hasCloudRecording === false) {
      return (
        <Paragraph>
          Cloud Recording is DISABLED for your Zoom account. Please enable it in Zoom settings if you want automatic
          video capture.
        </Paragraph>
      );
    } else {
      return <Paragraph>Cloud recording is enabled for your Zoom account.</Paragraph>;
    }
  };

  if (zoomIntegrationData) {
    return (
      <>
        <Paragraph>Your Zoom account is connected.</Paragraph>
        {getZoomCloudRecordingStatus()}
      </>
    );
  }

  return (
    <>
      <Paragraph displayType="loud">Zoom integration</Paragraph>
      {zoomIntegrationData ? (
        <>
          <Paragraph>Your Zoom account is connected.</Paragraph>
          {getZoomCloudRecordingStatus()}
        </>
      ) : (
        <Button
          text="Connect Zoom Account"
          disabled={loading}
          appearance="primary"
          onClick={handleConnectZoom}
          className="mt-2"
        />
      )}
    </>
  );
};
